import { Geogroup, GeogroupService, Partner, TSubGroupType } from '@geovelo-frontends/commons';
import { useQuery } from '@tanstack/react-query';

import { queryKeys } from './query-keys';
import { resolveQueryData } from './resolve-query-data';

export interface IPartnerSubgroups {
  regions: Geogroup[] | null;
  sites: Geogroup[] | null;
  subgroupTypes: TSubGroupType[];
  subsidiaries: Geogroup[] | null;
  teams: Geogroup[] | null;
}

const emptySubgroups: IPartnerSubgroups = {
  subgroupTypes: [],
  teams: null,
  subsidiaries: null,
  regions: null,
  sites: null,
};

const noGeogroupSubgroups: IPartnerSubgroups = {
  subgroupTypes: ['companySite', 'companyTeam'],
  teams: null,
  subsidiaries: null,
  regions: null,
  sites: null,
};

async function fetchPartnerSubgroups(geoGroupId: number): Promise<IPartnerSubgroups> {
  const subgroupTypes = await GeogroupService.getSubgroupTypes({ groupId: geoGroupId });

  const { count, geogroups } = await GeogroupService.getGeogroups({
    ordering: 'title,id',
    parentId: geoGroupId,
    types: subgroupTypes,
    pageSize: 100,
    page: 1,
  });

  const nbPages = Math.ceil(count / 100);
  if (nbPages > 1) {
    const _geogroups = (
      await Promise.all(
        new Array(nbPages - 1).fill(null).map((_, index) =>
          GeogroupService.getGeogroups({
            ordering: 'title,id',
            parentId: geoGroupId,
            types: subgroupTypes,
            pageSize: 100,
            page: 2 + index,
          }),
        ),
      )
    ).flatMap(({ geogroups }) => geogroups);

    geogroups.push(..._geogroups);
  }

  const sortByTitle = (a: Geogroup, b: Geogroup) =>
    a.title.toLowerCase().localeCompare(b.title.toLowerCase());

  return {
    subgroupTypes,
    teams: subgroupTypes.includes('companyTeam')
      ? geogroups.filter(({ subGroupType }) => subGroupType === 'companyTeam').sort(sortByTitle)
      : null,
    subsidiaries: subgroupTypes.includes('companySubsidiary')
      ? geogroups
          .filter(({ subGroupType }) => subGroupType === 'companySubsidiary')
          .sort(sortByTitle)
      : null,
    regions: subgroupTypes.includes('companyRegion')
      ? geogroups.filter(({ subGroupType }) => subGroupType === 'companyRegion').sort(sortByTitle)
      : null,
    sites: geogroups.filter(({ subGroupType }) => subGroupType === 'companySite').sort(sortByTitle),
  };
}

export default function usePartnerSubgroupsQuery(
  partner: Partner | null | undefined,
  { isHolding }: { isHolding?: boolean } = {},
) {
  const geoGroupId = partner?.geoGroupId;
  const enabled = !!partner && !!geoGroupId && !isHolding;

  const query = useQuery({
    queryKey: queryKeys.partnerSubgroups.all(partner?.id),
    queryFn: () => fetchPartnerSubgroups(geoGroupId as number),
    enabled,
    staleTime: 60_000,
  });

  const data = resolveQueryData(
    query.data,
    !partner
      ? undefined
      : isHolding
        ? emptySubgroups
        : !geoGroupId || query.isError
          ? noGeogroupSubgroups
          : undefined,
  );

  return { ...query, data };
}
